import { useDispatch, useSelector } from 'react-redux'
import { useMemo, useEffect } from 'react'
import deepEqual from 'fast-deep-equal'
import moment from 'moment'

import { useGetBookingsListQuery } from 'entities/bookings/model'
import { OrdersApi } from 'entities/orders/api'
import { RootState } from 'shared/redux/store'
import { closeModal, openModal } from 'shared/redux/slice/modalSlice'
import { closeSidebar } from 'shared/redux/slice/sidebar'
import {
    BookingMode,
    BookingStep,
    clearBookingState,
    OrderViewMode,
    removeBooking,
    selectActiveBookingId,
    selectBookingCounterparty,
    selectBookingCurrentStep,
    selectBookingFormMode,
    selectBookingFormOrderViewMode,
    selectBookingIdToEdit,
    selectBookingOrderId,
    selectBookingOriginalOrder,
    selectBookings,
    selectIsCompanyBookingForm,
    selectIsHistoryModeEnabled,
    selectIsRedirectForCounterpartyCreation,
    selectOrderEditStateInitialising,
    selectTimeFilter,
    setBookingFormOrderViewMode,
    setIsCompany,
    setIsDirty,
    setIsHistoryModeEnabled,
} from '../slices'

export const useBookingSidebarController = () => {
    const dispatch = useDispatch()
    const timeFilter = useSelector(selectTimeFilter)
    const orderViewMode = useSelector(selectBookingFormOrderViewMode)
    const isEditStateInitialising = useSelector(
        selectOrderEditStateInitialising,
    )
    const { refetch: refetchBookings } = useGetBookingsListQuery(timeFilter)
    const formCurrentStep = useSelector(selectBookingCurrentStep)

    const { isOpen } = useSelector((state: RootState) => state.sidebar)
    const mode = useSelector(selectBookingFormMode)
    const orderId = useSelector(selectBookingOrderId)
    const isCompany = useSelector(selectIsCompanyBookingForm)

    const isRedirectForCounterpartyCreation = useSelector(
        selectIsRedirectForCounterpartyCreation,
    )

    const counterparty = useSelector(selectBookingCounterparty)
    const counterpartyId = counterparty?.id ?? null
    const originalOrder = useSelector(selectBookingOriginalOrder)
    const currentFormBookings = useSelector(selectBookings)

    const previewBookingId = useSelector(selectBookingIdToEdit)
    const isEdit = useMemo(() => mode === BookingMode.Edit, [mode])
    const isRefund = useMemo(
        () =>
            isEdit &&
            orderViewMode === OrderViewMode.Refund &&
            !!previewBookingId,
        [orderViewMode, isEdit, previewBookingId],
    )
    const isHistory = useSelector(selectIsHistoryModeEnabled)
    const isPreview = useMemo(
        () =>
            isEdit &&
            orderViewMode === OrderViewMode.Preview &&
            !!previewBookingId,
        [orderViewMode, isEdit, previewBookingId],
    )

    const isPaid = useMemo(
        () =>
            !!originalOrder?.totalAmountPaid &&
            !!originalOrder?.totalPriceWithDiscount &&
            originalOrder?.totalAmountPaid > 0 &&
            originalOrder?.totalPriceWithDiscount > 0,
        [originalOrder],
    )

    const goFromPreviewToEdit = () => {
        dispatch(
            setBookingFormOrderViewMode({ orderViewMode: OrderViewMode.Edit }),
        )
        dispatch(
            setIsHistoryModeEnabled({
                isHistoryModeEnabled: false,
            }),
        )
    }
    const activeTabIndex = isCompany ? 1 : 0
    const disabledTabIndex = isEdit ? (isCompany ? 0 : 1) : undefined

    const activeBookingId = useSelector(selectActiveBookingId)
    const bookings = useSelector(selectBookings)
    const activeBooking = useMemo(
        () => bookings.find(a => a.id === activeBookingId),
        [bookings, activeBookingId],
    )

    const isPastBooking = useMemo(
        () =>
            activeBooking?.startTime
                ? moment(activeBooking.startTime).isBefore(moment())
                : false,
        [activeBooking],
    )

    const handleForceCloseSidebar = () => {
        dispatch(clearBookingState())
        dispatch(closeSidebar())
    }

    const handleCloseSidebar = () => {
        if (isRefund) {
            dispatch(
                setBookingFormOrderViewMode({
                    orderViewMode: OrderViewMode.Edit,
                }),
            )
            return
        }
        if (isHistory) {
            dispatch(
                setIsHistoryModeEnabled({
                    isHistoryModeEnabled: false,
                }),
            )
            return
        }
        handleForceCloseSidebar()
    }

    const deleteOrder = async (orderId: number) => {
        await OrdersApi.deleteOrder({ orderId })
        dispatch(clearBookingState())
        await refetchBookings()
        handleCloseSidebar()
    }

    const onDeleteOrder = async () => {
        if (!orderId) {
            const content = <>Вы уверены, что хотите сбросить бронь?</>
            const title =
                'Вы точно хотите сбросить этот заказ? Информация о броне будет сброшена'
            const btns = [
                {
                    onClick: () => {
                        handleCloseSidebar()
                        dispatch(closeModal())
                        dispatch(closeSidebar())
                    },
                    title: 'Да',
                    active: true,
                },
                {
                    onClick: () => dispatch(closeModal()),
                    title: 'Нет',
                    active: false,
                },
            ]

            dispatch(
                openModal({
                    isOpen: true,
                    content,
                    config: {
                        title,
                        btns,
                    },
                }),
            )
            return
        }

        if (isPaid) {
            return onDeleteOrderWithPayments(orderId)
        }

        const content = <>Вы уверены, что хотите удалить заказ?</>
        const title =
            'Вы точно хотите удалить этот заказ? Все его бронирования будут удалены.'
        const btns = [
            {
                onClick: () => {
                    deleteOrder(orderId)
                    dispatch(closeModal())
                },
                title: 'Да',
                active: true,
            },
            {
                onClick: () => dispatch(closeModal()),
                title: 'Нет',
                active: false,
            },
        ]

        dispatch(
            openModal({
                isOpen: true,
                content,
                config: {
                    title,
                    btns,
                },
            }),
        )
    }

    const onDeleteOrderWithPayments = async (orderId: number) => {
        const content = <>Заказ был оплачен</>
        const title = `В заказ было внесено ${originalOrder!.totalAmountPaid}₽. Что делать с деньгами?`
        const btns = [
            // {
            //     onClick: () => {
            //         deleteOrder(orderId)
            //         dispatch(closeModal())
            //     },
            //     title: 'Вернуть позже',
            //     active: false,
            // },
            {
                onClick: () => {
                    onGoRefund()
                    dispatch(closeModal())
                },
                title: 'Вернуть сейчас',
                active: false,
            },
            {
                onClick: () => {
                    deleteOrder(orderId)
                    dispatch(closeModal())
                },
                title: 'Оставить',
                active: false,
            },
            {
                onClick: () => dispatch(closeModal()),
                title: 'Не отменять заказ',
                active: false,
            },
        ]

        dispatch(
            openModal({
                isOpen: true,
                content,
                config: {
                    title,
                    btns,
                },
            }),
        )
    }

    const onRemoveBooking = async () => {
        if (!previewBookingId || !orderId) {
            return
        }

        const deleteBooking = async () => {
            await OrdersApi.deleteBookingFromOrder({
                orderId,
                bookingId: previewBookingId,
            })
            await refetchBookings()
        }
        const content = <>Вы уверены, что хотите удалить это бронирование?</>
        const title = 'Вы точно хотите удалить это бронирование?'
        const btns = [
            {
                onClick: () => {
                    deleteBooking().then()
                    dispatch(removeBooking({ bookingId: previewBookingId }))
                    dispatch(closeModal())
                    dispatch(closeSidebar())
                },
                title: 'Да',
                active: true,
            },
            {
                onClick: () => dispatch(closeModal()),
                title: 'Нет',
                active: false,
            },
        ]

        dispatch(
            openModal({
                isOpen: true,
                content,
                config: {
                    title,
                    btns,
                },
            }),
        )
    }

    const isDirty = useMemo(() => {
        if (mode === BookingMode.Create) {
            return !!counterpartyId && !orderId
        }

        if (mode === BookingMode.Edit) {
            if (!originalOrder) {
                return false
            }

            if (originalOrder.bookings.length !== currentFormBookings.length) {
                return true
            }

            if (
                counterpartyId &&
                originalOrder.counterpartyId !== counterpartyId
            ) {
                return true
            }

            return originalOrder.bookings.some(originalBooking => {
                const currentBooking = currentFormBookings.find(
                    booking => booking.id === originalBooking.id,
                )
                return !deepEqual(originalBooking, currentBooking)
            })
        }
        return false
    }, [mode, orderId, counterpartyId, currentFormBookings, originalOrder])

    useEffect(() => {
        dispatch(setIsDirty({ isDirty }))
    }, [isDirty])

    const onChangeTab = (index: number) => {
        const isDirtyCreation = isDirty && mode === BookingMode.Create
        const isNotDirtyCreation = !isDirty && mode === BookingMode.Create
        const isPassedFirstStep = formCurrentStep !== BookingStep.GeneralInfo

        console.log(index, activeTabIndex, isRedirectForCounterpartyCreation)

        if (isDirtyCreation || isPassedFirstStep) {
            const content =
                'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть ?'
            const title =
                'Вы точно хотите удалить этот заказ? Все его бронирования будут удалены.'
            const btns = [
                {
                    onClick: () => {
                        dispatch(clearBookingState())
                        dispatch(closeModal())
                        dispatch(
                            setIsCompany({
                                isCompany: index === 1,
                            }),
                        )
                    },
                    title: 'Да',
                    active: true,
                },
                {
                    onClick: () => dispatch(closeModal()),
                    title: 'Нет',
                    active: false,
                },
            ]

            dispatch(
                openModal({
                    isOpen: true,
                    content,
                    config: {
                        title,
                        btns,
                    },
                }),
            )
            return
        }

        if (isNotDirtyCreation) {
            dispatch(
                setIsCompany({
                    isCompany: index === 1,
                }),
            )
        }
    }

    const onGoToPaymentRedirectUrl = () => {
        const url = `${window.location.origin}/invoice/${orderId}`
        window?.open(url, '_blank')?.focus()
    }

    const onGoRefund = () => {
        dispatch(
            setBookingFormOrderViewMode({
                orderViewMode: OrderViewMode.Refund,
            }),
        )
        dispatch(
            setIsHistoryModeEnabled({
                isHistoryModeEnabled: false,
            }),
        )
    }

    const goToHistory = () => {
        dispatch(
            setIsHistoryModeEnabled({
                isHistoryModeEnabled: true,
            }),
        )
    }

    return {
        isHistory,
        isDirty,
        isOrderExist: !!orderId,
        isPastBooking,
        isRefund,
        isCompany,
        isEditStateInitialising,
        onGoToPaymentRedirectUrl,
        activeTabIndex,
        disabledTabIndex,
        isOpen,
        isPreview,
        isEdit,
        isPaid,
        goFromPreviewToEdit,
        onChangeTab,
        handleCloseSidebar,
        handleForceCloseSidebar,
        onDeleteOrder,
        onRemoveBooking,
        onGoRefund,
        goToHistory,
    }
}
